import type { SubscriptionStatus, BillingInterval } from './types';

export function formatTrialDaysLeft(trialEndsAt: string | null, now: Date = new Date()): number {
  if (!trialEndsAt) return 0;
  const ms = new Date(trialEndsAt).getTime() - now.getTime();
  return Math.max(0, Math.ceil(ms / 86_400_000));
}

export function formatMoney(amountCents: number | null, currency: string | null): string {
  if (amountCents === null || currency === null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amountCents / 100);
}

export function formatRenewalDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * The credit reset instant is a UTC month boundary (nexus
 * `credits::next_month_start`), so it must be rendered in UTC. Formatting it
 * in the viewer's zone shows "Aug 31" to anyone west of Greenwich.
 */
export function formatCreditResetDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function formatBillingCadence(interval: BillingInterval): string {
  return interval === 'year' ? 'Billed annually' : 'Billed monthly';
}

/**
 * Stripe reports the brand as a lowercase slug (`visa`, `mastercard`). Show it
 * the way the card itself and a bank statement do.
 */
export function formatCardBrand(brand: string | null): string | null {
  if (!brand) return null;
  const known: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
    diners: 'Diners Club',
    unionpay: 'UnionPay',
    jcb: 'JCB',
  };
  return known[brand] ?? brand.charAt(0).toUpperCase() + brand.slice(1);
}

/** `"Visa ••••4242"`, or null unless both halves are known. */
export function formatSavedCard(
  brand: string | null,
  last4: string | null,
): string | null {
  const name = formatCardBrand(brand);
  if (!name || !last4) return null;
  return `${name} ••••${last4}`;
}

export function statusTagColor(
  status: SubscriptionStatus['status'],
): 'success' | 'warning' | 'danger' {
  if (status === 'past_due') return 'warning';
  if (status === 'canceled') return 'danger';
  return 'success';
}
